import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnInit,
  Optional,
  Renderer2,
} from '@angular/core';
import {takeUntilDestroyed, UntilDestroy} from '@atlas-angular/rxjs';
import {CssClassUtility, CssClassUtilityFactory, Utilities} from '@maia/core';

import {CommonButton} from '../common-button.interface';
import {RadioGroupComponent} from '../radio-group/radio-group.component';
import {
  DEFAULT_RADIO_BUTTON_SIZE,
  RADIO_BUTTON_CLASSES,
  RadioButtonSize,
} from './radio-button.utils';

const generateId = Utilities.createIdGenerator('maia-radio-button');

/**
 * @ngModule RadioButtonsModule
 */
@Component({
  selector: 'maia-radio-button',
  templateUrl: './radio-button.component.html',
  styleUrls: ['./radio-button.component.scss'],
  host: {
    role: 'radio',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class RadioButtonComponent implements CommonButton, OnInit {
  private readonly _cssClassUtility: CssClassUtility<typeof RADIO_BUTTON_CLASSES>;
  private _disabled: boolean;
  public forceDisabled: boolean;
  private _size: RadioButtonSize;

  // The id of the radio button. An id is auto-generated in case no outside id is set. The id is
  // considered final once the component is set-up, and changing it breaks this component's
  // behaviour.
  @Input()
  @HostBinding('id')
  public id = generateId();

  @Input()
  public value: any;

  @Input()
  @HostBinding('class.maia-radio-button--disabled')
  public get disabled(): boolean {
    return this._disabled || this.forceDisabled;
  }

  public set disabled(disabled: boolean) {
    this._disabled = coerceBooleanProperty(disabled);
  }

  @Input()
  public set size(size: RadioButtonSize) {
    this._size = size;
    this._cssClassUtility.setValue('size', this._size);
  }

  public get size(): RadioButtonSize {
    return this._size;
  }

  @Input()
  @HostBinding('attr.aria-checked')
  @HostBinding('class.maia-radio-button--active')
  public active: boolean;

  @HostListener('click')
  public select(): void {
    if (!this.disabled && this.radioGroup != null) {
      this.radioGroup.selectedValue = this.value;
    }
  }

  public constructor(
    @Inject(forwardRef(() => RadioGroupComponent))
    @Optional()
    private readonly radioGroup: RadioGroupComponent,
    private readonly _changeDetector: ChangeDetectorRef,
    cssClassUtilityFactory: CssClassUtilityFactory,
    renderer: Renderer2,
    elementRef: ElementRef,
  ) {
    this._cssClassUtility = cssClassUtilityFactory.create(
      RADIO_BUTTON_CLASSES,
      renderer,
      elementRef,
    );
    this.size = DEFAULT_RADIO_BUTTON_SIZE;
  }
  private _updateActive(value: any): void {
    const active = this.value === value;

    if (active !== this.active) {
      this.active = active;
    }

    if (this.active && this.radioGroup != null) {
      this.radioGroup.activeDescendant = this.id;
    }
    this._changeDetector.detectChanges();
  }

  public ngOnInit(): void {
    if (this.radioGroup != null) {
      this.radioGroup.selectedValue$
        .pipe(takeUntilDestroyed(this))
        .subscribe(value => this._updateActive(value));
    }
  }
}
